<template>
  <div class="w-full flex justify-center">
    <Transition
      mode="out-in"
      enter-active-class="animate-in fade-in duration-200"
      leave-active-class="animate-out fade-out duration-200"
    >
      <AdvancedMT
        v-if="viewState === 'edit-mt'"
        key="edit-mt"
        @success="handleEditSuccess"
        @cancel="handleEditCancel"
      />

      <AdvancedRS
        v-else-if="viewState === 'edit-rs'"
        key="edit-rs"
        @success="handleEditSuccess"
        @cancel="handleEditCancel"
      />

      <AdvancedSOCD
        v-else-if="viewState === 'edit-socd'"
        key="edit-socd"
        @success="handleEditSuccess"
        @cancel="handleEditCancel"
      />

      <AdvancedTGL
        v-else-if="viewState === 'edit-tgl'"
        key="edit-tgl"
        @success="handleEditSuccess"
        @cancel="handleEditCancel"
      />

      <AdvancedDKS
        v-else-if="viewState === 'edit-dks'"
        key="edit-dks"
        @success="handleEditSuccess"
        @cancel="handleEditCancel"
      />

      <div v-else key="panel" class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
        <Transition
          mode="out-in"
          enter-active-class="animate-in fade-in duration-200"
          leave-active-class="animate-out fade-out duration-200"
        >
          <!-- 绑定列表 -->
          <div v-if="viewState === 'list'" key="list" class="flex flex-col gap-3 h-full">
            <div class="flex justify-between items-start">
              <div class="flex items-center gap-1">
                <span class="text-l-medium text-black-white">
                  {{ $t('keys.advanced.bindingList') }}
                </span>
              </div>
              <Button color="blue2" size="xs" @click="viewState = 'select-type'">
                <Icon icon="Function-Wireframe-Add11" />
                <span>{{ $t('keys.advanced.create') }}</span>
              </Button>
            </div>

            <Transition
              mode="out-in"
              enter-active-class="animate-in fade-in duration-200"
              leave-active-class="animate-out fade-out duration-200"
            >
              <div
                v-if="!advancedBindings.length"
                key="empty"
                class="flex-1 flex flex-col justify-center items-center gap-3"
              >
                <img :src="emptyTip" width="160" height="160" class="size-40" />
                <span class="text-m-medium text-text-glass-default">
                  {{ $t('keys.advanced.empty') }}
                </span>
              </div>

              <ScrollArea v-else key="items">
                <div class="grid grid-cols-2 gap-2">
                  <div
                    v-for="item in advancedBindings"
                    :key="item.id"
                    class="flex items-center justify-between gap-2.5 p-2 h-12 rounded-4xl bg-glass30"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <div
                        v-if="item.showPrimaryLabel"
                        class="p-2 flex items-center justify-center h-8 min-w-8 w-fit truncate bg-key rounded-t-l rounded-b-l"
                      >
                        <span class="text-l-medium text-black-white">
                          {{ item.primaryLabel }}
                        </span>
                      </div>
                      <div v-if="item.showPrimaryLabel" class="w-px h-2 bg-text-glass-hover" />
                      <div class="flex items-center gap-3">
                        <div
                          v-for="(label, labelIndex) in item.secondaryLabels"
                          :key="`${item.id}-${labelIndex}`"
                          class="p-2 flex items-center justify-center h-8 min-w-8 w-fit truncate bg-key rounded-t-l rounded-b-l"
                        >
                          <span class="text-l-medium text-black-white">
                            {{ label }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="shrink-0 flex items-center gap-3">
                      <div
                        :class="`px-0.5 font-[Barlow] rounded-t-l rounded-b-l bg-key text-l-medium ${getKeyTypeColorClass(item.keyType)}`"
                      >
                        {{ item.keyType }}
                      </div>
                      <Button color="normal" size="icon-xs" @click="handleEditBinding(item)">
                        <Icon icon="Writing-Wireframe-Edit" />
                      </Button>
                      <Button color="normal" size="icon-xs" @click="handleDeleteBinding(item)">
                        <Icon icon="Function-Wireframe-Delete" />
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </Transition>
          </div>

          <!-- 功能选择 -->
          <div v-else key="select-type" class="flex flex-col gap-3 h-full">
            <div class="flex justify-between items-start">
              <span class="text-l-medium text-black-white">
                {{ $t('keys.advanced.selectFunction') }}
              </span>
              <Button color="glass" size="xs" @click="viewState = 'list'">
                <span>{{ $t('common.cancel') }}</span>
              </Button>
            </div>

            <div class="flex-1 grid grid-cols-2 gap-2 overflow-y-auto">
              <div
                v-for="mode in advancedModeItems"
                :key="mode.type"
                :class="`group  ${mode.isFull ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`"
                @click="handleSelectMode(mode.type)"
              >
                <div
                  :class="`p-2 flex items-center gap-1 rounded-4xl bg-key transition-colors ${mode.isFull ? '' : 'group-hover:bg-white'}`"
                >
                  <Icon
                    :icon="mode.icon"
                    :class="[
                      'w-8 h-8 transition-colors text-text-glass-default',
                      `${mode.isFull ? '' : 'group-hover:text-brand-blue2'}`,
                    ]"
                  />
                  <div class="flex flex-col gap-1">
                    <h3
                      :class="`text-m-medium text-text-glass-default transition-colors ${mode.isFull ? '' : 'group-hover:text-brand-blue2'}`"
                    >
                      {{ mode.title }}
                    </h3>
                    <p
                      :class="`text-s-medium text-text-glass-default transition-colors ${mode.isFull ? '' : 'group-hover:text-blue-dark-hover'}`"
                    >
                      {{ mode.excerpt }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('keys.advanced.deleteDialog.title')"
      content-class="w-90"
      :close-on-overlay-click="!isDeleting"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('keys.advanced.deleteDialog.content') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" :disabled="isDeleting" @click="isDeleteDialogOpen = false">
            {{ $t('common.cancel') }}
          </Button>
          <Button color="text-b-w" :disabled="isDeleting" @click="handleConfirmDelete">
            <Icon v-if="isDeleting" icon="Function-Wireframe-CW" class="animate-spin" />
            {{ $t('common.delete') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, ref, watch, provide } from 'vue'
  import { Button, Dialog, Icon, ScrollArea, toast } from '@rapoo/element'
  import type { KeyConfig } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import emptyTip from '@/assets/images/devices/keys/empty.webp'
  import AdvancedMT from './components/AdvancedMT.vue'
  import AdvancedRS from './components/AdvancedRS.vue'
  import AdvancedSOCD from './components/AdvancedSOCD.vue'
  import AdvancedTGL from './components/AdvancedTGL.vue'
  import AdvancedDKS from './components/AdvancedDKS.vue'
  import { KEYCODE_KEYWORDS_MAP } from '../../utils/keyMapSettings'
  import { parseNumber } from '../../utils/keyIdentity'
  import type { AdvancedSlotKind } from '../../utils/advancedKeyMapSettings'
  import { useAdvancedKeyCommit, type AdvancedSlotUsage } from './composables/useAdvancedKeyCommit'
  import {
    KeySettingsSymbol,
    RefreshLayerKeySettingsSymbol,
    RestoreKeyMappingWithTargetSymbol,
    SelectedKeySymbol,
    AdvancedEditingSettingSymbol,
    SuppressSelectedKeyUxSymbol,
    type KeySetting,
    type RestoreKeyMappingWithTarget,
    type SelectedKey,
  } from '../../types'

  const { t } = useLocaleStore()
  const { loadAdvancedSlotUsage, clearSocdKeyTravel } = useAdvancedKeyCommit()

  type AdvancedModeType = 'rs' | 'dks' | 'socd' | 'mt' | 'tgl'
  type ViewState =
    | 'list'
    | 'select-type'
    | 'edit-mt'
    | 'edit-rs'
    | 'edit-socd'
    | 'edit-tgl'
    | 'edit-dks'
  type AdvancedKeyType = 'MT' | 'RS' | 'SOCD' | 'DKS' | 'TGL'
  type AdvancedBinding = {
    id: string
    keyType: AdvancedKeyType
    keywords: string
    primaryLabel: string
    secondaryLabels: string[]
    showPrimaryLabel: boolean
    setting: KeySetting
  }

  const ADVANCED_KEY_TYPE_COLOR: Record<AdvancedKeyType, string> = {
    MT: 'text-brand-orange',
    RS: 'text-purple-active',
    SOCD: 'text-brand-red',
    DKS: 'text-blue-light-active',
    TGL: 'text-green-active',
  }

  /**
   * 每种高级按键占用的信息区与 slot 数：
   * MT / RS / SOCD 共用 MT 信息区，RS、SOCD 一对键各占 1 个 slot（合计 2 个）。
   */
  const ADVANCED_MODE_SLOT: Record<
    AdvancedModeType,
    { kind: AdvancedSlotKind; cost: number; label: AdvancedKeyType }
  > = {
    mt: { kind: 'mt', cost: 1, label: 'MT' },
    tgl: { kind: 'tgl', cost: 1, label: 'TGL' },
    rs: { kind: 'mt', cost: 2, label: 'RS' },
    socd: { kind: 'mt', cost: 2, label: 'SOCD' },
    dks: { kind: 'dks', cost: 1, label: 'DKS' },
  }

  const getKeyTypeColorClass = (keyType: AdvancedKeyType) => {
    return ADVANCED_KEY_TYPE_COLOR[keyType]
  }

  const viewState = ref<ViewState>('list')
  const editingSetting = ref<KeySetting | null>(null)
  const isDeleting = ref(false)
  const isDeleteDialogOpen = ref(false)
  const pendingDeleteBinding = ref<AdvancedBinding | null>(null)
  const slotUsage = ref<AdvancedSlotUsage | null>(null)
  const suppressSelectedKeyUx = inject(SuppressSelectedKeyUxSymbol, ref(false))
  const keySettings = inject(KeySettingsSymbol)
  const selectedKey = inject(SelectedKeySymbol)
  const refreshLayerKeySettings = inject(RefreshLayerKeySettingsSymbol)
  const restoreKeyMappingWithTarget = inject<RestoreKeyMappingWithTarget>(
    RestoreKeyMappingWithTargetSymbol,
  )

  provide(AdvancedEditingSettingSymbol, editingSetting)

  watch(
    viewState,
    (state) => {
      suppressSelectedKeyUx.value = state === 'edit-rs' || state === 'edit-socd'
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    suppressSelectedKeyUx.value = false
  })

  const mtBindings = computed(() =>
    (keySettings?.value ?? [])
      .filter((setting) => setting.keyType === 'MT')
      .map(
        (setting): AdvancedBinding => ({
          id: `mt-${setting.keywords}`,
          keyType: 'MT',
          keywords: setting.keywords,
          primaryLabel: setting.raw?.originalLabel || setting.keywords,
          secondaryLabels: [setting.raw?.clickKeyLabel || '-', setting.raw?.pressKeyLabel || '-'],
          showPrimaryLabel: true,
          setting,
        }),
      ),
  )

  const rsBindings = computed(() => {
    const seen = new Set<string>()
    const pairs: AdvancedBinding[] = []

    for (const setting of keySettings?.value ?? []) {
      if (setting.keyType !== 'RS') continue
      const peer = (setting.raw?.relatedKeyKeywords as string | undefined) ?? ''
      const id = [setting.keywords, peer].sort().join('+')
      if (seen.has(id)) continue
      seen.add(id)
      pairs.push({
        id: `rs-${id}`,
        keyType: 'RS',
        keywords: setting.keywords,
        primaryLabel: setting.raw?.originalLabel || setting.keywords,
        secondaryLabels: [
          setting.raw?.originalLabel || setting.keywords,
          setting.raw?.relatedKeyLabel || peer || '-',
        ],
        showPrimaryLabel: false,
        setting,
      })
    }

    return pairs
  })

  const socdBindings = computed(() => {
    const seen = new Set<string>()
    const pairs: AdvancedBinding[] = []

    for (const setting of keySettings?.value ?? []) {
      if (setting.keyType !== 'SOCD') continue
      const peer = (setting.raw?.relatedKeyKeywords as string | undefined) ?? ''
      const id = [setting.keywords, peer].sort().join('+')
      if (seen.has(id)) continue
      seen.add(id)
      pairs.push({
        id: `socd-${id}`,
        keyType: 'SOCD',
        keywords: setting.keywords,
        primaryLabel: setting.raw?.originalLabel || setting.keywords,
        secondaryLabels: [
          setting.raw?.originalLabel || setting.keywords,
          setting.raw?.relatedKeyLabel || peer || '-',
        ],
        showPrimaryLabel: false,
        setting,
      })
    }

    return pairs
  })

  const dksBindings = computed(() =>
    (keySettings?.value ?? [])
      .filter((setting) => setting.keyType === 'DKS')
      .map(
        (setting): AdvancedBinding => ({
          id: `dks-${setting.keywords}`,
          keyType: 'DKS',
          keywords: setting.keywords,
          primaryLabel: setting.raw?.originalLabel || setting.keywords,
          secondaryLabels: (setting.raw?.dksKeyLabels as string | undefined)?.split('/') ?? ['-'],
          showPrimaryLabel: true,
          setting,
        }),
      ),
  )

  const tglBindings = computed(() =>
    (keySettings?.value ?? [])
      .filter((setting) => setting.keyType === 'TGL')
      .map(
        (setting): AdvancedBinding => ({
          id: `tgl-${setting.keywords}`,
          keyType: 'TGL',
          keywords: setting.keywords,
          primaryLabel: setting.raw?.originalLabel || setting.keywords,
          secondaryLabels: [setting.raw?.clickKeyLabel || '-'],
          showPrimaryLabel: true,
          setting,
        }),
      ),
  )

  const advancedBindings = computed(() => [
    ...socdBindings.value,
    ...rsBindings.value,
    ...dksBindings.value,
    ...tglBindings.value,
    ...mtBindings.value,
  ])

  const advancedModes = computed(() => [
    {
      type: 'mt' as const,
      title: t('keys.advanced.modes.mt.title'),
      excerpt: t('keys.advanced.modes.mt.excerpt'),
      icon: 'Technology-Wireframe-QuickTrigger',
    },
    {
      type: 'tgl' as const,
      title: t('keys.advanced.modes.tgl.title'),
      excerpt: t('keys.advanced.modes.tgl.excerpt'),
      icon: 'Multimedia-Wireframe-SwitchAxis',
    },
    {
      type: 'rs' as const,
      title: t('keys.advanced.modes.rs.title'),
      excerpt: t('keys.advanced.modes.rs.excerpt'),
      icon: 'Technology-Fill-Rapid',
    },
    {
      type: 'socd' as const,
      title: t('keys.advanced.modes.socd.title'),
      excerpt: t('keys.advanced.modes.socd.excerpt'),
      icon: 'Multimedia-Wireframe-Copy',
    },
    {
      type: 'dks' as const,
      title: t('keys.advanced.modes.dks.title'),
      excerpt: t('keys.advanced.modes.dks.excerpt'),
      icon: 'Technology-Wireframe-KeyBottomDZ',
    },
  ])

  /** 该类型剩余可新建数量；信息区尚未读到时返回 undefined，表示未知不做限制。 */
  const getModeRemaining = (type: AdvancedModeType) => {
    const usage = slotUsage.value?.[ADVANCED_MODE_SLOT[type].kind]
    if (!usage || usage.total <= 0) return undefined
    return usage.total - usage.used
  }

  const isModeFull = (type: AdvancedModeType) => {
    const remaining = getModeRemaining(type)
    return remaining != null && remaining < ADVANCED_MODE_SLOT[type].cost
  }

  const advancedModeItems = computed(() =>
    advancedModes.value.map((mode) => ({ ...mode, isFull: isModeFull(mode.type) })),
  )

  const refreshSlotUsage = async () => {
    slotUsage.value = await loadAdvancedSlotUsage()
  }

  onMounted(refreshSlotUsage)

  const selectKeyByKeywords = (keywords: string) => {
    if (!selectedKey) return
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)
    selectedKey.value = config ?? null
  }

  const handleSelectMode = (type: AdvancedModeType) => {
    if (type !== 'mt' && type !== 'rs' && type !== 'socd' && type !== 'tgl' && type !== 'dks') {
      toast.info(t('toast.keys.comingSoon'))
      return
    }

    if (isModeFull(type)) {
      toast.info(t('toast.keys.advancedTypeFull', { type: ADVANCED_MODE_SLOT[type].label }))
      return
    }

    editingSetting.value = null
    if (selectedKey) selectedKey.value = null
    if (type === 'rs') {
      viewState.value = 'edit-rs'
      return
    }
    if (type === 'socd') {
      viewState.value = 'edit-socd'
      return
    }
    if (type === 'tgl') {
      viewState.value = 'edit-tgl'
      return
    }
    if (type === 'dks') {
      viewState.value = 'edit-dks'
      return
    }

    viewState.value = 'edit-mt'
  }

  const handleEditBinding = (item: AdvancedBinding) => {
    editingSetting.value = item.setting
    if (item.keyType === 'RS') viewState.value = 'edit-rs'
    else if (item.keyType === 'SOCD') viewState.value = 'edit-socd'
    else {
      selectKeyByKeywords(item.keywords)
      if (item.keyType === 'TGL') viewState.value = 'edit-tgl'
      else if (item.keyType === 'DKS') viewState.value = 'edit-dks'
      else viewState.value = 'edit-mt'
    }
  }

  /** 组装恢复默认所需的目标键：优先用绑定里记录的矩阵 index。 */
  const toRestoreTarget = (keywords?: string, index?: unknown): SelectedKey => {
    if (!keywords) return null
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)
    if (!config) return null
    const matrixIndex = parseNumber(index)
    return matrixIndex == null ? config : ({ ...config, key: matrixIndex } as KeyConfig)
  }

  /** RS / SOCD 是一对键的绑定，删除时两个键都要恢复。 */
  const collectDeleteTargets = (item: AdvancedBinding): KeyConfig[] => {
    const targets = [toRestoreTarget(item.keywords, item.setting.raw?.index)]

    if (item.keyType === 'RS' || item.keyType === 'SOCD') {
      targets.push(
        toRestoreTarget(
          item.setting.raw?.relatedKeyKeywords as string | undefined,
          item.setting.raw?.relatedKeyIndex,
        ),
      )
    }

    return targets.filter((target): target is KeyConfig => Boolean(target))
  }

  const handleDeleteBinding = (item: AdvancedBinding) => {
    if (isDeleting.value) return
    pendingDeleteBinding.value = item
    isDeleteDialogOpen.value = true
  }

  watch(isDeleteDialogOpen, (open) => {
    if (!open && !isDeleting.value) pendingDeleteBinding.value = null
  })

  /**
   * 删除一条高级按键绑定：把涉及的物理键写回默认功能，
   * SDK 的 setLayerKeysMap 会在离开高级类型时顺带清空其引用的信息区 slot。
   */
  const handleConfirmDelete = async () => {
    const item = pendingDeleteBinding.value
    if (!item || isDeleting.value) return

    const targets = collectDeleteTargets(item)
    if (!targets.length) {
      isDeleteDialogOpen.value = false
      pendingDeleteBinding.value = null
      return
    }

    isDeleting.value = true
    try {
      for (const target of targets) {
        await restoreKeyMappingWithTarget?.(target)
      }
      if (item.keyType === 'SOCD') await clearSocdKeyTravel(item.setting)

      await refreshLayerKeySettings?.()
      await refreshSlotUsage()
      isDeleteDialogOpen.value = false
      pendingDeleteBinding.value = null
      toast.success(t('toast.keys.advancedDeleted'))
    } catch (error) {
      console.error('Failed to delete advanced binding', error)
      toast(t('toast.device.fail'))
    } finally {
      isDeleting.value = false
    }
  }

  const handleEditSuccess = async () => {
    await refreshLayerKeySettings?.()
    await refreshSlotUsage()
    viewState.value = 'list'
    editingSetting.value = null
    if (selectedKey) selectedKey.value = null
  }

  const handleEditCancel = () => {
    viewState.value = 'select-type'
    editingSetting.value = null
    if (selectedKey) selectedKey.value = null
  }
</script>

<style scoped></style>
